import React, { useState } from "react";
import {
    UploadOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import {
    Input,
    Button,
    Upload,
    Spin,
} from "antd";

const PlaylistItem = ({ item, index, fileItems, setFileItems }) => {
    const [interval, setInterval] = useState(item.interval || "");
    const [description, setDescription] = useState(item.description || "");
    const [loading, setLoading] = useState(false); // New loading state

    const sessionToken = localStorage.getItem("sessionToken");
    
    const typewriterEffect = async (text) => {
        setDescription(""); 
            for (let i = 0; i < text.length; i++) {
                await new Promise((resolve) => setTimeout(resolve, 35)); // This is milliseconds per character
                setDescription((prev) => prev + text[i]);
            } 
    };

    const uploadProps = {
        onRemove: (file) => {
            const newFileItems = [...fileItems];
            newFileItems[index].file = null;
            setFileItems(newFileItems);
        },
        beforeUpload: async (file) => {
            const newFileItems = [...fileItems];
            newFileItems[index].file = file;
            setFileItems(newFileItems);

            setLoading(true); // Set loading to true
            const formData = new FormData();
            formData.append("image", file);
            try {
                // Check for any inappropriate content in the image
                const inappropriateUrl = new URL('/api/system/ai/postImageAppropriateFile', window.location.origin);
                const inappropriateResponse = await fetch(inappropriateUrl, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        ContentType: "multipart/form-data",
                        Authorization: `Bearer ${sessionToken}`,
                    },
                });
                if (!inappropriateResponse.ok) {
                    throw new Error(`HTTP Error, network call failed. Status: ${inappropriateResponse.status}`);
                }
                const inappropriateData = await inappropriateResponse.json();
                
                // inappropriateData is a nested object with keys: inappropriate, nudity, curse_words, other_inappropriate_content
                console.log("Inappropriate content check: ", inappropriateData);
                if (inappropriateData.inappropriate) {
                    // If inappropriate content is found, remove the playlistItem, alert and stop running uploadProps function.
                    const newFileItems = [...fileItems];
                    newFileItems.splice(index, 1); 
                    setFileItems(newFileItems); 
                    setLoading(false); 

                    if (inappropriateData.nudity) {
                        alert("Image potentially contains nudity or other inappropriate content. If you think this is a mistake, please try uploading again.");
                    } 
                    else if (inappropriateData.curse_words) {
                        alert("Image potentially contains curse words or other inappropriate content. If you think this is a mistake, please try uploading again.");
                    } 
                    else { 
                        alert("Image potentially contains inappropriate content. If you think this is a mistake, please try uploading again.");
                    }
                    return false; 
                }
                else {
                    // If no inappropriate content is found, proceed to get the image description
                    console.log("No inappropriate content found.");
                }

                // Get the image description from AI
                const url = new URL('/api/system/ai/postImageDescriptionFile', window.location.origin);
                const response = await fetch(url, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        ContentType: "multipart/form-data",
                        Authorization: `Bearer ${sessionToken}`,
                    },
                });
                if (!response.ok) {
                    throw new Error(`HTTP Error, network call failed. Status: ${response.status}`);
                }
                
                const data = await response.json();
                const suggestedDescription = data.description || "No description available";

                await typewriterEffect(suggestedDescription); 
            } catch (error) {
                console.error("Error fetching image description:", error);
                setDescription("No Description available");
            } finally {
                setLoading(false); // Set loading to false
            }

            return false;
        },
    };

    const onItemRemove = () => {
        const newFileItems = [...fileItems];
        newFileItems.splice(index, 1);
        for (let i = 0, len = newFileItems.length; i < len; i++) {
            if (newFileItems[i].index !== i) {
                newFileItems[i].index = i;
            }
        }
        setFileItems(newFileItems);
    };

    const onIntervalChange = (e) => {
        setInterval(e.target.value);
    };

    const onDescriptionChange = (e) => {
        setDescription(e.target.value);
    };

    const onIntervalBlur = (e) => {
        const value = e.target.value;
        if (!value) {
            const newFileItems = [...fileItems];
            newFileItems[index].interval = null;
            setFileItems(newFileItems);
        } else if (!isNaN(value) && value.trim() !== "") {
            const newFileItems = [...fileItems];
            newFileItems[index].interval = value;
            setFileItems(newFileItems);
        } else {
            alert("Please enter a valid number");
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                marginBottom: 5,
                width: "100%",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    gap: 20,
                    flexWrap: "wrap",
                }}
            >
                <Upload {...uploadProps}>
                    <Button
                        disabled={fileItems[index].file}
                        icon={<UploadOutlined />}
                    >
                        Upload File
                    </Button>
                </Upload>
                <Input
                    placeholder={"Insert Interval in Seconds"}
                    onChange={onIntervalChange}
                    onBlur={onIntervalBlur}
                    value={interval}
                    style={{ flex: "0 1 150px", minWidth: "60px" }}
                ></Input>
                <Input
                    placeholder={"Enter a Description"}
                    onChange={onDescriptionChange}
                    value={description}
                    disabled={loading} // Disable input while loading
                    style={{ flex: "1 1 auto", minWidth: "200px" }}
                ></Input>
                <Button
                    onClick={onItemRemove}
                    icon={<DeleteOutlined />}
                    style={{ flex: "0 0 auto" }}
                ></Button>
            </div>
            {loading && (
                <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                    style={{ marginTop: 10 }}
                />
            )}
        </div>
    );
};

export default PlaylistItem;